import { DrawProduct, View } from "@novorender/api";
import { useEffect, useRef, useState } from "react";

import { isAxisPartRedundant } from "features/engine2D";

import { PointLine } from "../pointLineSlice";

/**
 * Find draw product for each point line if firstTwoPointAxisMeasurements is present.
 */
export function usePointToPointAxisDrawProduct(view: View | undefined, pointLines: PointLine[]) {
    const [drawProducts, setDrawProducts] = useState<(DrawProduct | undefined)[]>([]);

    const cacheRef = useRef(new WeakMap<PointLine, DrawProduct | undefined>());

    useEffect(() => {
        const abortController = new AbortController();
        getDrawProduct();

        async function getDrawProduct() {
            const measureView = view?.measure;
            if (!measureView) {
                return;
            }

            const promises = pointLines.map(async (line) => {
                if (!line.firstTwoPointAxisMeasurements) {
                    return;
                }

                let value = cacheRef.current.get(line);
                if (!value) {
                    value = await measureView.draw.getDrawEntity(line.firstTwoPointAxisMeasurements);

                    // Keep only ...-axis parts and ensure they are not too close to the original line
                    const [{ position: p1 }, { position: p2 }] = line.points;
                    const newParts = value.objects[0].parts.filter(
                        (part) =>
                            ["x-axis", "y-axis", "z-axis"].includes(part.name!) && !isAxisPartRedundant(part, p1, p2),
                    );

                    value = { ...value, objects: [{ ...value.objects[0], parts: newParts }] };
                    cacheRef.current.set(line, value);
                }

                return value;
            });

            const axisDrawProducts = await Promise.all(promises);
            if (abortController.signal.aborted) {
                return;
            }

            setDrawProducts(axisDrawProducts);
        }

        return () => {
            abortController.abort();
        };
    }, [view, pointLines]);

    return drawProducts;
}
